import React, { useEffect, useRef } from "react";

/**
 * Animated "neuron mapping" background for the hero section.
 *
 * Renders a field of softly glowing light dots (neurons) that drift slowly
 * and connect to nearby dots with faint synapse-like lines. Bright signal
 * pulses occasionally travel along the connections, and the whole network
 * gently bends toward the mouse, with links near the cursor lighting up.
 */

const CONFIG = {
  baseDensity: 11000, // px^2 of canvas area per neuron (lower = more dots)
  maxNeurons: 220,
  minNeurons: 70,
  linkDistance: 150, // max distance for a synapse line
  mouseRadius: 240, // radius of mouse influence
  mousePull: 52, // max px a neuron bends toward the cursor
  mouseEase: 0.1, // how quickly neurons bend / spring back
  driftSpeed: 0.22,
  maxPulses: 7,
  pulseChance: 0.03, // chance per frame to spawn a signal pulse
  dotRGB: "201, 180, 255", // soft violet, matches the AI accent
  lineRGB: "155, 109, 255",
};

function createNeuron(width, height) {
  const angle = Math.random() * Math.PI * 2;
  const speed = (0.3 + Math.random() * 0.7) * CONFIG.driftSpeed;
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    // Elastic display offset toward the cursor (springs back to zero)
    ox: 0,
    oy: 0,
    r: 0.9 + Math.random() * 1.7,
    phase: Math.random() * Math.PI * 2, // twinkle offset
    twinkle: 0.5 + Math.random() * 1.2, // twinkle speed
  };
}

export default function NeuralBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");

    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let neurons = [];
    let pulses = [];
    let rafId = 0;
    let time = 0;
    const mouse = { x: -9999, y: -9999, active: false };

    function resize() {
      const parent = canvas.parentElement;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = parent ? parent.offsetWidth : window.innerWidth;
      height = parent ? parent.offsetHeight : window.innerHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const target = Math.max(
        CONFIG.minNeurons,
        Math.min(CONFIG.maxNeurons, Math.round((width * height) / CONFIG.baseDensity))
      );
      while (neurons.length < target) neurons.push(createNeuron(width, height));
      neurons.length = target;
    }

    function onMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active =
        mouse.x >= 0 && mouse.y >= 0 && mouse.x <= width && mouse.y <= height;
    }

    function onMouseLeave() {
      mouse.active = false;
    }

    function spawnPulse() {
      // Pick a random neuron and one of its actual neighbours so the
      // signal visibly travels along an existing synapse line.
      const from = neurons[(Math.random() * neurons.length) | 0];
      if (!from) return;
      let best = null;
      let bestDist = CONFIG.linkDistance;
      for (let i = 0; i < 12; i++) {
        const candidate = neurons[(Math.random() * neurons.length) | 0];
        if (candidate === from) continue;
        const d = Math.hypot(candidate.x - from.x, candidate.y - from.y);
        if (d < bestDist && d > 20) {
          best = candidate;
          bestDist = d;
        }
      }
      if (best) {
        pulses.push({ from, to: best, t: 0, speed: 0.008 + Math.random() * 0.012 });
      }
    }

    function step() {
      time += 0.016;

      for (let i = 0; i < neurons.length; i++) {
        const n = neurons[i];

        if (!reduceMotion) {
          n.x += n.vx;
          n.y += n.vy;
        }

        // Bend elastically toward the cursor; spring back when it leaves
        let tx = 0;
        let ty = 0;
        if (mouse.active) {
          const dx = mouse.x - n.x;
          const dy = mouse.y - n.y;
          const dist = Math.hypot(dx, dy);
          if (dist < CONFIG.mouseRadius && dist > 1) {
            const f = 1 - dist / CONFIG.mouseRadius;
            const pull = f * f * CONFIG.mousePull;
            tx = (dx / dist) * pull;
            ty = (dy / dist) * pull;
          }
        }
        n.ox += (tx - n.ox) * CONFIG.mouseEase;
        n.oy += (ty - n.oy) * CONFIG.mouseEase;

        // Wrap softly around the edges
        if (n.x < -20) n.x = width + 20;
        else if (n.x > width + 20) n.x = -20;
        if (n.y < -20) n.y = height + 20;
        else if (n.y > height + 20) n.y = -20;
      }
    }

    function draw() {
      ctx.clearRect(0, 0, width, height);

      const linkDistSq = CONFIG.linkDistance * CONFIG.linkDistance;

      // Synapse lines
      for (let i = 0; i < neurons.length; i++) {
        const a = neurons[i];
        const axp = a.x + a.ox;
        const ayp = a.y + a.oy;
        for (let j = i + 1; j < neurons.length; j++) {
          const b = neurons[j];
          const bxp = b.x + b.ox;
          const byp = b.y + b.oy;
          const dx = axp - bxp;
          const dy = ayp - byp;
          const distSq = dx * dx + dy * dy;
          if (distSq >= linkDistSq) continue;

          const dist = Math.sqrt(distSq);
          let alpha = (1 - dist / CONFIG.linkDistance) * 0.16;

          // Links near the cursor glow brighter
          if (mouse.active) {
            const mx = (axp + bxp) / 2 - mouse.x;
            const my = (ayp + byp) / 2 - mouse.y;
            const mDist = Math.hypot(mx, my);
            if (mDist < CONFIG.mouseRadius) {
              alpha += (1 - mDist / CONFIG.mouseRadius) * 0.22;
            }
          }

          ctx.strokeStyle = "rgba(" + CONFIG.lineRGB + ", " + alpha + ")";
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.moveTo(axp, ayp);
          ctx.lineTo(bxp, byp);
          ctx.stroke();
        }
      }

      // Signal pulses travelling along synapses
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        p.t += p.speed;
        if (p.t >= 1) {
          pulses.splice(i, 1);
          continue;
        }
        const fx = p.from.x + p.from.ox;
        const fy = p.from.y + p.from.oy;
        const x = fx + (p.to.x + p.to.ox - fx) * p.t;
        const y = fy + (p.to.y + p.to.oy - fy) * p.t;
        const fade = Math.sin(p.t * Math.PI); // ease in and out
        const glow = ctx.createRadialGradient(x, y, 0, x, y, 7);
        glow.addColorStop(0, "rgba(234, 221, 255, " + 0.9 * fade + ")");
        glow.addColorStop(1, "rgba(155, 109, 255, 0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fill();
      }

      // Neurons (light dots with a soft halo and slow twinkle)
      for (let i = 0; i < neurons.length; i++) {
        const n = neurons[i];
        const px = n.x + n.ox;
        const py = n.y + n.oy;
        const twinkle = 0.55 + 0.45 * Math.sin(time * n.twinkle + n.phase);
        let brightness = 0.35 + 0.45 * twinkle;

        if (mouse.active) {
          const mDist = Math.hypot(px - mouse.x, py - mouse.y);
          if (mDist < CONFIG.mouseRadius) {
            brightness += (1 - mDist / CONFIG.mouseRadius) * 0.35;
          }
        }

        const haloR = n.r * 4;
        const halo = ctx.createRadialGradient(px, py, 0, px, py, haloR);
        halo.addColorStop(0, "rgba(" + CONFIG.dotRGB + ", " + 0.35 * brightness + ")");
        halo.addColorStop(1, "rgba(" + CONFIG.dotRGB + ", 0)");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(px, py, haloR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "rgba(" + CONFIG.dotRGB + ", " + Math.min(brightness, 1) + ")";
        ctx.beginPath();
        ctx.arc(px, py, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function frame() {
      step();
      if (
        !reduceMotion &&
        pulses.length < CONFIG.maxPulses &&
        Math.random() < CONFIG.pulseChance
      ) {
        spawnPulse();
      }
      draw();
      rafId = window.requestAnimationFrame(frame);
    }

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseleave", onMouseLeave);
    rafId = window.requestAnimationFrame(frame);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}
